// "Remind me if they don't reply": chosen at the last look before a send (the composer's review
// and a card's send strip). On, the conversation goes to Waiting for a reply and a nudge comes
// back after the chosen weekdays if nobody answers; off, it is marked handled once sent.
import { BellRing } from "lucide-react";
import type { ComposeRemindDays } from "@shared/api";
import { cn } from "@web/lib/cn";
import { REMIND_CHOICES } from "./compose";

export interface RemindChoice {
  on: boolean;
  days: Exclude<ComposeRemindDays, 0>;
}

export function RemindToggle({
  value,
  onChange,
  disabled,
}: {
  value: RemindChoice;
  onChange: (next: RemindChoice) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-paper px-3 py-2.5">
      <label className="flex items-start gap-2.5 text-[15px] text-ink">
        <input
          type="checkbox"
          role="switch"
          checked={value.on}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, on: e.target.checked })}
          className="mt-1 size-4 accent-[var(--accent)]"
          data-testid="compose-remind"
        />
        <span className="flex min-w-0 flex-col">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <BellRing aria-hidden className="size-4 text-muted" />
            Remind me if they don&rsquo;t reply
          </span>
          <span className="text-sm text-muted">
            {value.on
              ? "It goes to Waiting for a reply and comes back to you if nobody answers."
              : "Off: this conversation is marked handled once it's sent."}
          </span>
        </span>
      </label>
      {value.on ? (
        <div
          role="radiogroup"
          aria-label="Remind me after"
          className="flex flex-wrap items-center gap-1.5 pl-6.5 text-sm"
        >
          <span className="text-muted">After</span>
          {REMIND_CHOICES.map((days) => (
            <button
              key={days}
              type="button"
              role="radio"
              aria-checked={value.days === days}
              disabled={disabled}
              onClick={() => onChange({ on: true, days })}
              className={cn(
                "h-8 min-w-9 rounded-md border px-2 text-ink-2",
                value.days === days
                  ? "border-accent/60 bg-accent-soft text-accent-text"
                  : "border-line-strong bg-paper hover:bg-sunk",
              )}
            >
              {days}
            </button>
          ))}
          <span className="text-muted">weekdays</span>
        </div>
      ) : null}
    </div>
  );
}
